import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { DEV_URL } from '@/lib/constants'
import { Dialog, DialogTitle, DialogDescription, DialogBody, DialogActions } from '@/components/catalyst-ui/dialog'
import { Button } from '@/components/catalyst-ui/button'
import { XMarkIcon } from '@heroicons/react/24/solid'
import { buildWithAI, buildWithAITemplates, startFromBlank } from '@/assets/images'
import { PinwheelLoader } from '@/components/ui/PinwheelLoader'
import { useWorkspaces } from '@/features/workspace/hooks/useWorkspaces'
import { useSelectionStore } from '@/state/selection.store'
import { trackEvent } from '@/lib/analytics'

function OptionCard({ title, subtitle, onClick, image }) {
  const [isImageLoaded, setIsImageLoaded] = useState(false)
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex flex-col rounded-xl border-2 border-[#E4E4E7] bg-white p-2 pb-4 text-left shadow-[0px_0px_0px_1px_#0000001A] transition-all duration-200 hover:shadow-[0px_0px_20px_0px_#00000029] cursor-pointer hover:border-[#5C33FF80] data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
    >
      <div className="relative mb-4 overflow-hidden rounded-lg border border-[#E4E4E7] bg-[#FAFAFA]">
        {!isImageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <PinwheelLoader size={20} className="bg-transparent" />
          </div>
        )}
        <img
          src={image}
          alt={title}
          onLoad={() => setIsImageLoaded(true)}
          className={`w-full object-contain transition-transform duration-200 group-hover:scale-105 ${
            isImageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
        />
      </div>
       <div className="px-1 text-lg lg:text-xl font-semibold text-black/70 group-hover:text-black">
        {title}
      </div>
      <div className="mt-1 px-1 text-sm lg:text-base font-medium text-[#A1A1AA]">
        {subtitle}
      </div>
    </button>
  )
}

export function CreateProjectDialog({isOpen, onClose}) {
  const navigate = useNavigate()
  const { workspaces } = useWorkspaces()
  const storeWorkspaceId = useSelectionStore(state => state.activeWorkspaceId)
  const urlWorkspaceId = new URLSearchParams(window.location.search).get('workspace')
  const currentWsId = urlWorkspaceId || storeWorkspaceId
  const activeWorkspace = workspaces?.find(ws => (ws._id || ws.id) === currentWsId) || workspaces?.[0]
  
  const handleClose = () => {
    onClose?.()
  }

  // Track dialog open
  useEffect(() => {
    if (isOpen) {
      trackEvent('CreateProject:DialogOpened', { workspaceId: activeWorkspace?._id })
    }
  }, [isOpen])

  return (
    <Dialog
      size="xl"
      className="!rounded-xl !max-w-[900px] p-12 !bg-[#F4F4F5] !shadow-[0px_4px_4px_0px_#00000040,0px_0px_0px_4px_#FFFFFF60]"
      open={isOpen}
      onClose={handleClose}
    >
      <div className="flex items-start justify-between mb-4">
        <DialogTitle className="!text-3xl !font-semibold text-[#09090B] text-center w-full mb-6 -mt-2">
          Select a way to get started
        </DialogTitle>
        <button
          type="button"
          onClick={handleClose}
          className="text-[#000000] hover:text-[#52525B] cursor-pointer -mr-4 data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2 rounded-full"
        >
          <XMarkIcon className="w-5 h-5 -mt-9 -mr-12" />
        </button>
      </div>

      <DialogDescription className="sr-only">
        Choose how you want to create your new site.
      </DialogDescription>

      <DialogBody className="mt-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <OptionCard
            title="Build with AI"
            subtitle="Create a website with AI"
            onClick={() => {
              trackEvent('CreateProject:BuildWithAI', { workspaceId: activeWorkspace?._id })
              handleClose()
              navigate(`/ai${activeWorkspace?._id ? `?workspace=${activeWorkspace._id}` : ''}`)
            }}
            image={buildWithAI}
          />
          <OptionCard
            title="Build with AI Templates"
            subtitle="Choose template from library"
            onClick={() => {
              trackEvent('CreateProject:BuildWithTemplates', { workspaceId: activeWorkspace?._id })
              window.location.href = 'https://codedesign.ai/templates'
            }}
            image={buildWithAITemplates}
          />
          <OptionCard
            title="Start from Blank"
            subtitle="Create project from blank"
            onClick={() => {
              trackEvent('CreateProject:StartFromBlank', { workspaceId: activeWorkspace?._id })
              window.location.href = `${DEV_URL}/template-library?templateId=63dced9c2e86d81a23e82e13&action=step-two`
            }}
            image={startFromBlank}
          />
        </div>

        <div className="mt-6 flex items-center justify-center">
          <span className="text-base font-semibold text-[#71717A]">or</span>
        </div>

        <div className="mt-4 flex justify-center">
          <Button
            color="dark"
            className="px-6 py-2 text-sm font-semibold whitespace-nowrap data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
            type="button"
            onClick={() => {
              trackEvent('CreateProject:HireExpert', { workspaceId: activeWorkspace?._id })
              window.open('https://tally.so/r/mBpXQ4', '_blank', 'noopener,noreferrer')
            }}
          >
            Hire an Expert to Build your Project
          </Button>
        </div>
      </DialogBody>
      <DialogActions className="hidden" />
    </Dialog>
  )
}


